import { Injectable } from "@angular/core";
import { Action, Selector, State, StateContext, Store } from "@ngxs/store";
import { EMPTY } from "rxjs";
import { mergeMap, tap } from "rxjs/operators";
import { AuthUserLoginAction } from "src/app/auth/state/auth.actions";
import { ILogin } from "src/app/model/IAuth";
import { IPortalAuth } from "src/app/model/IPortal";
import { StoredAuthRemoveAction } from "src/app/shared/state/stored-auth.actions";
import { StoredAuthState } from "src/app/shared/state/stored-auth.state";
import { PortalService } from "../service/portal.service";
import {
    PortalAuthUserLoginAction,
    PortalAuthUserLogoutAction,
    PortalAuthValidatePortalKeyAction
} from "./portal-auth.actions";
import { PortalState } from "./portal.state";

export class PortalAuthStateModel {
	public isFinishedLoading: boolean;
	public authKey: string;
	public seoName: string;

	public isAuthKeyValid: boolean;
	public errors: string;
}

@State<PortalAuthStateModel>({
	name: "portalAuth",
	defaults: {
		isFinishedLoading: true,
		authKey: null,
		seoName: null,
		isAuthKeyValid: false,
		errors: null,
	},
})
@Injectable()
export class PortalAuthState {
	constructor(private store: Store, private portalService: PortalService) {}

	@Selector()
	static authKey(state: PortalAuthStateModel) {
		return state.authKey;
	}
	@Selector()
	static isAuthKeyValid(state: PortalAuthStateModel): boolean {
		return state.isAuthKeyValid;
	}

	@Selector()
	static seoName(state: PortalAuthStateModel) {
		return state.seoName;
	}

	@Selector()
	static errors(state: PortalAuthStateModel): string {
		return state.errors;
	}

	@Selector([PortalAuthState, StoredAuthState.isUserAuthenticated])
	static isLoggedInAndAuthorized(
		state: PortalAuthStateModel,
		isUserAuthenticated
	): boolean {
		return state.isAuthKeyValid && isUserAuthenticated;
	}

	/************************************* */

	@Action(PortalAuthValidatePortalKeyAction)
	ValidatePortalKeyAction(
		ctx: StateContext<PortalAuthStateModel>,
		action: PortalAuthValidatePortalKeyAction
	) {
		const seoName = this.store.selectSnapshot(PortalState.seoName);
		const data: IPortalAuth = {
            authKey: action.payload.authKey,
            
		};
		return this.portalService.isAuthKeyValid(seoName, data).pipe(
			tap((result: any) => {
				ctx.patchState({
					isAuthKeyValid: result ? true : false,
                    authKey: action.payload.authKey,
                    seoName
				});
			})
		);
	}

	@Action(PortalAuthUserLoginAction)
	UserLoginAction(
		ctx: StateContext<PortalAuthStateModel>,
		action: PortalAuthUserLoginAction
	) {
		ctx.patchState({
			isFinishedLoading: false,
			errors: "",
		});
		return ctx.dispatch(new PortalAuthValidatePortalKeyAction(action.payload)).pipe(
			mergeMap(() => {
				const { isAuthKeyValid } = ctx.getState();
				if (!isAuthKeyValid) {
					ctx.patchState({
						errors: "Invalid Portal Auth key ",
					});
					return EMPTY;
				}
				const data: ILogin = {
					username: action.payload.username,
					password: action.payload.password,
				};
				return ctx.dispatch([new AuthUserLoginAction(data)]);
			})
		);
	}
	@Action(PortalAuthUserLogoutAction)
	PortalAuthUserLogoutAction(
		ctx: StateContext<PortalAuthStateModel>,
		action: PortalAuthUserLogoutAction
	) {
		ctx.patchState({
            authKey: null,
			isAuthKeyValid: false,
		});
		return ctx.dispatch(new StoredAuthRemoveAction());
	}
}
